import { sendGroupBusinessInvite } from '@disco/state-management/actions/GroupActions'
import { Box, Flex, Link, Text, TextField } from '@radix-ui/themes'

import React, { useCallback, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import { Button } from 'components/ui'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'

type Props = {
  channelId: number
}
/**
 * SharePartnerDisco component for sharing channels with partner organizations
 *
 * A functional component that provides an interface for sharing channels with partner
 * DISCO organizations. Users can enter a partner's DISCO URL and send an invitation.
 * Includes environment-specific domain handling and analytics tracking.
 *
 * @param props - Component props
 * @param props.channelId - ID of the channel to share
 * @returns JSX element containing the partner sharing interface
 */
export const SharePartnerDisco = ({ channelId }: Props): React.ReactNode => {
  const [inputValue, setInputValue] = useState('')
  const dispatch = useDispatch()
  const { hostname } = window.location
  const [loading, setLoading] = useState(false)
  const domain =
    hostname.includes('localhost') ||
    hostname.includes('disco-staging-2') ||
    hostname.includes('.dsc.party')
      ? '.disco-staging-2.com'
      : '.disco.ac'
  const handleSubmit = useCallback(
    async e => {
      if (!inputValue?.length) return
      e.preventDefault()
      setLoading(true)
      await dispatch(sendGroupBusinessInvite(channelId, inputValue))
      dispatch(
        sendMixpanelEvent('Click button', {
          Context: 'Shared Channels',
          Copy: 'Share',
          Type: 'Channel invitation',
          Location: 'Explorer',
          'Invite Url': inputValue,
          'Channel ID': channelId,
        }),
      )
      setInputValue('')
      setLoading(false)
    },
    [inputValue, channelId],
  )
  return (
    <Flex direction="column" width="100%" gap="1">
      <Text as="p" size="1">
        Enter your partner’s DISCO URL.{' '}
        <Link
          target="_blank"
          rel="noopener noreferrer"
          href="https://school.disco.ac/article/sharing-channels-and-folders"
        >
          Learn more
        </Link>
      </Text>
      <Flex gap="2">
        <Box flexGrow="1">
          <TextField.Root
            value={inputValue}
            onChange={e => setInputValue(e.target.value.toLowerCase())}
            placeholder="my-music-company"
          >
            <TextField.Slot side="right">{domain}</TextField.Slot>
          </TextField.Root>
        </Box>
        <Box>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            appearance="primary"
            caption="Share"
          />
        </Box>
      </Flex>
    </Flex>
  )
}
